import {
    Box,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormHelperText,
    FormLabel,
    Radio,
    RadioGroup,
    TextField,
    Typography,
} from '@mui/material';
import { JSX } from 'react';
import { Controller, Control, FieldErrors, UseFormRegister } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { EvaluationFormValues } from '../../../../library/models/evaluation';
import { fieldProps } from '../../../../library/formValidation';
import { CHILD_AGE_BOUNDS, numericSlotProps } from '../../../../library/validationRules';
import { SignaturePad } from '../../../signaturePad/SignaturePad';
import ResourcesIds from '../../../../resources/ResourcesIds';
import {
    childAgeRowStyles,
    consentCheckboxStyles,
    consentIntroStyles,
    contraindicationItemStyles,
    contraindicationsBoxStyles,
    declarationStyles,
    fieldGridStyles,
    narrowGridStyles,
    sectionTitleStyles,
    stackedFieldsStyles,
} from '../evaluation.styles';

interface ConsentStepProps {
    register: UseFormRegister<EvaluationFormValues>;
    control: Control<EvaluationFormValues>;
    errors: FieldErrors<EvaluationFormValues>;
    /** Drives whether the child's age is asked for. */
    isSigningForChild: boolean;
}

/**
 * The eighteen contraindications, in the order the paper form prints them.
 *
 * Numbered keys rather than one blob of text: they are a list a therapist reads out
 * item by item, and a translator who has to keep eighteen semicolons in the right places
 * inside a single string will not.
 */
const CONTRAINDICATION_KEYS: string[] = [
    ResourcesIds.EVALUATION_CONTRAINDICATION_1,
    ResourcesIds.EVALUATION_CONTRAINDICATION_2,
    ResourcesIds.EVALUATION_CONTRAINDICATION_3,
    ResourcesIds.EVALUATION_CONTRAINDICATION_4,
    ResourcesIds.EVALUATION_CONTRAINDICATION_5,
    ResourcesIds.EVALUATION_CONTRAINDICATION_6,
    ResourcesIds.EVALUATION_CONTRAINDICATION_7,
    ResourcesIds.EVALUATION_CONTRAINDICATION_8,
    ResourcesIds.EVALUATION_CONTRAINDICATION_9,
    ResourcesIds.EVALUATION_CONTRAINDICATION_10,
    ResourcesIds.EVALUATION_CONTRAINDICATION_11,
    ResourcesIds.EVALUATION_CONTRAINDICATION_12,
    ResourcesIds.EVALUATION_CONTRAINDICATION_13,
    ResourcesIds.EVALUATION_CONTRAINDICATION_14,
    ResourcesIds.EVALUATION_CONTRAINDICATION_15,
    ResourcesIds.EVALUATION_CONTRAINDICATION_16,
    ResourcesIds.EVALUATION_CONTRAINDICATION_17,
    ResourcesIds.EVALUATION_CONTRAINDICATION_18,
];

/** Page two of the paper form: identity, capacity, and consent to be treated. */
export const ConsentStep = (props: ConsentStepProps): JSX.Element => {
    const { t: translate } = useTranslation();
    const { register, control, errors, isSigningForChild } = props;

    return (
        <Box>
            <Typography variant="h6" sx={sectionTitleStyles}>
                {translate(ResourcesIds.EVALUATION_CONSENT_TITLE)}
            </Typography>

            <Box sx={stackedFieldsStyles}>
                <TextField
                    {...register('patientAddress')}
                    fullWidth
                    label={translate(ResourcesIds.EVALUATION_ADDRESS_LABEL)}
                    {...fieldProps(errors, 'patientAddress')}
                />

                <Box sx={narrowGridStyles}>
                    <TextField
                        {...register('idSeries')}
                        fullWidth
                        label={translate(ResourcesIds.EVALUATION_ID_SERIES_LABEL)}
                        {...fieldProps(errors, 'idSeries')}
                    />
                    <TextField
                        {...register('idNumber')}
                        fullWidth
                        label={translate(ResourcesIds.EVALUATION_ID_NUMBER_LABEL)}
                        {...fieldProps(errors, 'idNumber')}
                    />
                </Box>
            </Box>

            <FormControl sx={{ mt: 3 }}>
                <FormLabel>{translate(ResourcesIds.EVALUATION_CAPACITY_LABEL)}</FormLabel>
                <Controller
                    name="consentCapacity"
                    control={control}
                    render={({ field }) => (
                        <RadioGroup {...field}>
                            <FormControlLabel
                                value="PATIENT"
                                control={<Radio />}
                                label={translate(ResourcesIds.EVALUATION_CAPACITY_PATIENT)}
                            />
                            <FormControlLabel
                                value="LEGAL_REPRESENTATIVE"
                                control={<Radio />}
                                label={translate(ResourcesIds.EVALUATION_CAPACITY_REPRESENTATIVE)}
                            />
                        </RadioGroup>
                    )}
                />
            </FormControl>

            {/* Only asked when it means something. On the paper form this is a blank on
                the line nobody fills in unless they ticked the second box. */}
            {isSigningForChild && (
                <Box sx={childAgeRowStyles}>
                    <TextField
                        {...register('childAge')}
                        fullWidth
                        slotProps={numericSlotProps(CHILD_AGE_BOUNDS)}
                        type="number"
                        label={translate(ResourcesIds.EVALUATION_CHILD_AGE_LABEL)}
                        {...fieldProps(errors, 'childAge')}
                    />
                </Box>
            )}

            {/* No field here for what the treatment is, and no sentence naming it either.

                It was a free-text input, which asked somebody -- often the patient, since this
                is the step where they are handed the device -- to describe in their own words
                something with nothing to decide about it. The page already says what this
                consents to: the section is headed Consent to treatment, the list below is the
                contraindications for physiotherapy / massage, and the checkbox under it is the
                consent itself. The document still prints the treatment on its own line; see
                documents/evaluation.xhtml. */}
            <Box sx={{ ...fieldGridStyles, mt: 3 }}>
                <TextField
                    {...register('referringDoctor')}
                    fullWidth
                    label={translate(ResourcesIds.EVALUATION_REFERRING_DOCTOR_LABEL)}
                    {...fieldProps(errors, 'referringDoctor')}
                />
            </Box>

            <Typography variant="h6" sx={sectionTitleStyles}>
                {translate(ResourcesIds.EVALUATION_CONTRAINDICATIONS_TITLE)}
            </Typography>

            <Typography sx={consentIntroStyles}>
                {translate(ResourcesIds.EVALUATION_CONTRAINDICATIONS_INTRO)}
            </Typography>

            <Box sx={contraindicationsBoxStyles} component="ul">
                {CONTRAINDICATION_KEYS.map((key) => (
                    <Typography key={key} component="li" sx={contraindicationItemStyles}>
                        {translate(key)}
                    </Typography>
                ))}
            </Box>

            <Typography sx={declarationStyles}>
                {translate(ResourcesIds.EVALUATION_TREATMENT_DECLARATION)}
            </Typography>

            <FormControl error={!!errors.treatmentConsentGiven} sx={consentCheckboxStyles}>
                <Controller
                    name="treatmentConsentGiven"
                    control={control}
                    render={({ field }) => (
                        <FormControlLabel
                            control={<Checkbox {...field} checked={field.value} />}
                            label={translate(ResourcesIds.EVALUATION_TREATMENT_CONSENT_LABEL)}
                        />
                    )}
                />
                {errors.treatmentConsentGiven && (
                    <FormHelperText>{errors.treatmentConsentGiven.message}</FormHelperText>
                )}
            </FormControl>

            {/* The signature belongs to this consent, which is why it is on this step.

                It used to sit with the data-processing agreement, and the same drawing was
                printed under both -- which read as one signing until the agreement stopped
                being on every sheet. A returning patient never sees that step, so a
                signature living there would have left their treatment consent unsigned. */}
            <TextField
                {...register('signedName')}
                fullWidth
                required
                label={translate(ResourcesIds.EVALUATION_SIGNED_NAME_LABEL)}
                {...fieldProps(errors, 'signedName')}
            />

            {/* Controller rather than register: the pad is a canvas, not an input, so
                there is no ref for react-hook-form to attach to and no change event to
                listen for. */}
            <Controller
                name="signature"
                control={control}
                render={({ field }) => (
                    <SignaturePad
                        value={field.value}
                        onChange={field.onChange}
                        label={translate(ResourcesIds.EVALUATION_SIGNATURE_LABEL)}
                        hint={translate(ResourcesIds.EVALUATION_SIGNATURE_HINT)}
                        clearLabel={translate(ResourcesIds.EVALUATION_SIGNATURE_CLEAR)}
                        placeholder={translate(ResourcesIds.EVALUATION_SIGNATURE_PLACEHOLDER)}
                        {...fieldProps(errors, 'signature')}
                    />
                )}
            />
        </Box>
    );
};
